import { useEffect, useMemo, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { api, clearAdminToken } from "@/lib/api";
import { LogOut, Search, Loader2, ChevronRight } from "lucide-react";
import BrandLogo from "@/components/BrandLogo";

const STATUSES = [
    { v: "", l: "All" },
    { v: "new", l: "New" },
    { v: "in_review", l: "In review" },
    { v: "approved", l: "Approved" },
    { v: "rejected", l: "Rejected" },
];

function StatusBadge({ status }) {
    const map = {
        new: { color: "#60A5FA", label: "New" },
        in_review: { color: "#D4AF37", label: "In review" },
        approved: { color: "#4ADE80", label: "Approved" },
        rejected: { color: "#F87171", label: "Rejected" },
    };
    const m = map[status] || { color: "#888", label: status };
    return (
        <span className="badge-pill" data-testid={`status-badge-${status}`}>
            <span className="badge-dot" style={{ background: m.color, boxShadow: `0 0 8px ${m.color}` }} />
            <span className="text-white/80">{m.label}</span>
        </span>
    );
}

export default function AdminDashboard() {
    const navigate = useNavigate();
    const [loading, setLoading] = useState(true);
    const [query, setQuery] = useState("");
    const [status, setStatus] = useState("");
    const [items, setItems] = useState([]);
    const [total, setTotal] = useState(0);

    const load = async () => {
        setLoading(true);
        try {
            const { data } = await api.get("/admin/submissions", {
                params: { q: query || undefined, status: status || undefined, limit: 100 },
            });
            setItems(data.items || []);
            setTotal(data.total || 0);
        } catch (e) {
            if (e?.response?.status === 401) {
                clearAdminToken();
                navigate("/admin/login");
            }
        } finally {
            setLoading(false);
        }
    };

    useEffect(() => {
        load();
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [status]);

    const logout = () => {
        clearAdminToken();
        navigate("/admin/login");
    };

    const stats = useMemo(() => {
        const c = { new: 0, in_review: 0, approved: 0, rejected: 0 };
        items.forEach((i) => (c[i.status] = (c[i.status] || 0) + 1));
        return c;
    }, [items]);

    return (
        <div className="min-h-screen luxury-bg text-white" data-testid="admin-dashboard">
            <header className="border-b border-[#D4AF37]/20 bg-[#0a1628]/80 backdrop-blur-xl sticky top-0 z-30">
                <div className="max-w-7xl mx-auto px-6 py-4 flex items-center justify-between">
                    <div className="flex items-center gap-3">
                        <BrandLogo size="sm" testId="brand-logo-admin" />
                        <div className="text-[10px] uppercase tracking-[0.2em] text-white/50">Submissions Dashboard</div>
                    </div>
                    <button onClick={logout} className="text-sm text-white/70 hover:text-[#D4AF37] inline-flex items-center gap-2" data-testid="admin-logout-btn">
                        <LogOut size={14} /> Sign out
                    </button>
                </div>
            </header>

            <main className="max-w-7xl mx-auto px-6 py-8 space-y-8">
                {/* Stats */}
                <section className="grid grid-cols-2 lg:grid-cols-4 gap-4" data-testid="admin-stats">
                    {STATUSES.filter((s) => s.v).map((s) => (
                        <div key={s.v} className="rounded-xl border border-white/10 bg-white/[0.03] p-5">
                            <div className="field-label">{s.l}</div>
                            <div className="font-display text-4xl mt-2 text-white">{stats[s.v] || 0}</div>
                        </div>
                    ))}
                </section>

                {/* Filters */}
                <section className="flex flex-col md:flex-row gap-3 md:items-center md:justify-between">
                    <div className="relative flex-1 max-w-md">
                        <Search size={16} className="absolute left-3 top-1/2 -translate-y-1/2 text-white/40" />
                        <input
                            value={query}
                            onChange={(e) => setQuery(e.target.value)}
                            onKeyDown={(e) => e.key === "Enter" && load()}
                            placeholder="Search by name, email, phone, or ID…"
                            className="glow-input w-full pl-9 pr-3 py-2.5 rounded-xl text-sm"
                            data-testid="admin-search-input"
                        />
                    </div>
                    <div className="flex items-center gap-2 flex-wrap">
                        {STATUSES.map((s) => (
                            <button
                                key={s.v || "all"}
                                onClick={() => setStatus(s.v)}
                                className={`px-3 py-1.5 rounded-full text-xs border transition ${
                                    status === s.v
                                        ? "border-[#D4AF37]/60 bg-[#D4AF37]/15 text-white"
                                        : "border-white/10 bg-white/[0.02] text-white/60 hover:border-white/25"
                                }`}
                                data-testid={`filter-${s.v || "all"}`}
                            >
                                {s.l}
                            </button>
                        ))}
                        <button onClick={load} className="btn-gold px-4 py-1.5 text-xs" data-testid="admin-search-btn">
                            Search
                        </button>
                    </div>
                </section>

                {/* Table */}
                <section className="rounded-xl border border-white/10 bg-white/[0.02] overflow-hidden">
                    <div className="px-5 py-3 border-b border-white/5 flex items-center justify-between">
                        <div className="font-display text-lg">Submissions</div>
                        <div className="text-xs text-white/40">{total} total</div>
                    </div>

                    {loading ? (
                        <div className="py-16 flex items-center justify-center">
                            <Loader2 className="animate-spin text-[#D4AF37]" />
                        </div>
                    ) : items.length === 0 ? (
                        <div className="py-16 text-center text-white/50 text-sm" data-testid="admin-empty">
                            No submissions match your filters yet.
                        </div>
                    ) : (
                        <div className="overflow-x-auto">
                            <table className="w-full text-sm" data-testid="admin-submissions-table">
                                <thead>
                                    <tr className="text-left text-[10px] uppercase tracking-[0.18em] text-white/40">
                                        <th className="px-5 py-3 font-normal">Applicant</th>
                                        <th className="px-5 py-3 font-normal">Contact</th>
                                        <th className="px-5 py-3 font-normal">Submitted</th>
                                        <th className="px-5 py-3 font-normal">Status</th>
                                        <th className="px-5 py-3 font-normal" />
                                    </tr>
                                </thead>
                                <tbody>
                                    {items.map((it) => (
                                        <tr
                                            key={it.submission_id}
                                            className="admin-table-row cursor-pointer"
                                            onClick={() => navigate(`/admin/submissions/${it.submission_id}`)}
                                            data-testid={`row-${it.submission_id}`}
                                        >
                                            <td className="px-5 py-4">
                                                <div className="text-white">
                                                    {(it.applicant?.first_name || "") + " " + (it.applicant?.last_name || "")}
                                                </div>
                                                <div className="text-xs text-white/40 font-mono">{it.submission_id.slice(0, 8)}…</div>
                                            </td>
                                            <td className="px-5 py-4">
                                                <div className="text-white/80">{it.applicant?.email}</div>
                                                <div className="text-xs text-white/40">{it.applicant?.mobile}</div>
                                            </td>
                                            <td className="px-5 py-4 text-white/70">
                                                {it.submitted_at ? new Date(it.submitted_at).toLocaleString() : "—"}
                                            </td>
                                            <td className="px-5 py-4">
                                                <StatusBadge status={it.status} />
                                            </td>
                                            <td className="px-5 py-4 text-right">
                                                <ChevronRight size={16} className="inline text-white/40" />
                                            </td>
                                        </tr>
                                    ))}
                                </tbody>
                            </table>
                        </div>
                    )}
                </section>
            </main>
        </div>
    );
}
